<template>
  <div class="report-filters">
    <div class="rf-controls">
      <div v-if="showDates" class="rf-field">
        <label :for="uid + '-start'">From</label>
        <input
          :id="uid + '-start'"
          type="date"
          :value="startDate"
          @change="emitValue('startDate', $event.target.value)"
        />
      </div>

      <div v-if="showDates" class="rf-field">
        <label :for="uid + '-end'">To</label>
        <input
          :id="uid + '-end'"
          type="date"
          :value="endDate"
          @change="emitValue('endDate', $event.target.value)"
        />
      </div>

      <div v-if="showWing" class="rf-field">
        <label :for="uid + '-wing'">Wing</label>
        <select
          :id="uid + '-wing'"
          :value="wing"
          @change="emitValue('wing', $event.target.value)"
        >
          <option value="all">All wings</option>
          <option v-for="w in wings" :key="w.wing_id" :value="w.wing_id">
            {{ w.wing_name }}
          </option>
        </select>
      </div>

      <div v-if="showRoomType" class="rf-field">
        <label :for="uid + '-type'">Type</label>
        <select
          :id="uid + '-type'"
          :value="roomType"
          @change="emitValue('roomType', $event.target.value)"
        >
          <option value="all">All types</option>
          <option v-for="t in roomTypes" :key="t" :value="t">{{ t }}</option>
        </select>
      </div>

      <div v-if="showCategory" class="rf-field">
        <label :for="uid + '-category'">Category</label>
        <select
          :id="uid + '-category'"
          :value="serviceCategory"
          @change="emitValue('serviceCategory', $event.target.value)"
        >
          <option value="all">All categories</option>
          <option value="tenant">Tenant</option>
          <option value="parking">Parking</option>
          <option value="advertising">Advertising</option>
          <option value="other">Other</option>
        </select>
      </div>

      <button
        v-if="isFiltered"
        type="button"
        class="rf-reset"
        title="Clear all filters"
        @click="resetAll"
      >
        <i class="fa fa-rotate-left"></i>
        Reset
      </button>
    </div>

    <div v-if="$slots.actions" class="rf-actions">
      <slot name="actions"></slot>
    </div>

    <p v-if="note" class="rf-note">
      <i class="fa fa-circle-info"></i>
      {{ note }}
    </p>
  </div>
</template>

<script>
/**
 * Shared filter bar for every report page: date range, wing, room type and service category.
 *
 * Each control is opt-in via a `show*` prop, and each value is a separate v-model so a report
 * can keep its existing data fields (`v-model:start-date="startDate"` …). Emits `change` after
 * any value updates, so reports just re-fetch on `@change`.
 *
 * Wings are fetched here once so no report has to duplicate the lookup.
 */
const ROOM_TYPES = [
  "Apartment",
  "Studio",
  "Office",
  "Shop",
  "Warehouse",
  "Parking",
  "Storage",
  "Advertising",
  "Other",
];

let counter = 0;

export default {
  name: "ReportFilters",
  props: {
    startDate: { type: String, default: "" },
    endDate: { type: String, default: "" },
    roomType: { type: String, default: "all" },
    serviceCategory: { type: String, default: "all" },
    wing: { type: String, default: "all" },
    showDates: { type: Boolean, default: true },
    showRoomType: { type: Boolean, default: true },
    showCategory: { type: Boolean, default: true },
    showWing: { type: Boolean, default: true },
    /** Optional caveat shown under the bar (e.g. "expenses aren't wing-scoped"). */
    note: { type: String, default: "" },
  },
  emits: [
    "update:startDate",
    "update:endDate",
    "update:roomType",
    "update:serviceCategory",
    "update:wing",
    "change",
  ],
  data() {
    counter += 1;
    return {
      uid: `rf${counter}`,
      roomTypes: ROOM_TYPES,
      wings: [],
    };
  },
  computed: {
    /** Any non-default filter active — drives the Reset button. */
    isFiltered() {
      return (
        (this.showWing && this.wing !== "all") ||
        (this.showRoomType && this.roomType !== "all") ||
        (this.showCategory && this.serviceCategory !== "all")
      );
    },
  },
  methods: {
    emitValue(key, value) {
      this.$emit(`update:${key}`, value);
      // Let the parent's v-model settle before it re-fetches.
      this.$nextTick(() => this.$emit("change"));
    },
    resetAll() {
      if (this.showWing) this.$emit("update:wing", "all");
      if (this.showRoomType) this.$emit("update:roomType", "all");
      if (this.showCategory) this.$emit("update:serviceCategory", "all");
      this.$nextTick(() => this.$emit("change"));
    },
    fetchWings() {
      if (!this.showWing) return;
      this.$store
        .dispatch("GET_WINGS", {
          token: this.$getAdminToken(),
          data: { house_id: this.$getHouseId(), paginate: 500, page: 1 },
        })
        .then((res) => {
          if (res && res.status === 200) {
            const d = res.data;
            this.wings =
              d.wings?.data || d.data?.data || d.wings || d.data || [];
          }
        })
        .catch(() => {
          /* non-fatal — the wing selector just stays empty */
        });
    },
  },
  mounted() {
    this.fetchWings();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/main.scss";

.report-filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  padding: 0.85rem 1rem;
  margin-bottom: 1.5rem;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.rf-controls {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.rf-field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 0;

  label {
    font-size: 0.68rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #8a8fa3;
  }

  input,
  select {
    height: 36px;
    padding: 0 0.65rem;
    border: 1px solid #e2e5ea;
    border-radius: 8px;
    background: #fff;
    font-size: 0.85rem;
    color: #333;
    outline: none;
    transition: border-color 0.15s, box-shadow 0.15s;
    max-width: 190px;

    &:focus {
      border-color: $primary_color;
      box-shadow: 0 0 0 3px rgba($primary_color, 0.1);
    }
  }

  select {
    cursor: pointer;
    min-width: 130px;
  }
}

.rf-reset {
  height: 36px;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0 0.75rem;
  border: 1px solid #e2e5ea;
  border-radius: 8px;
  background: #fff;
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #f87171;
    color: #b91c1c;
    background: #fef2f2;
  }
}

.rf-actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.rf-note {
  flex-basis: 100%;
  margin: 0;
  padding-top: 0.15rem;
  font-size: 0.78rem;
  color: #92400e;

  i {
    margin-right: 0.35rem;
    color: #d97706;
  }
}

@media (max-width: 768px) {
  .report-filters {
    flex-direction: column;
    align-items: stretch;
  }
  .rf-field input,
  .rf-field select {
    max-width: none;
    width: 100%;
  }
  .rf-field {
    flex: 1 1 140px;
  }
}
</style>
